import React, { useEffect, useState } from 'react';
import { IconButton } from '@mui/material';
import BTable from '../../../Tools/Table/BTable';
import { useTranslation } from 'react-i18next';
import { AddCircle, Edit } from '@mui/icons-material';
import ModalWithLoader, {
  ModalWithLoaderAction,
  RequestStatus
} from '../../../Tools/ModalWithLoader/ModalWithLoader';
import FP2ESensorBrandForm from './FP2ESensorBrandForm';
import { useGetCompanies } from '../../../../hooks/datarefs';
import {
  BirdzDialog,
  BirdzNotif,
  useDialog,
  useNotif
} from '@applications-terrains/birdz-react-library';
import useRefresh from '../../../../hooks/useRefresh';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import { FP2EItem } from './types';

export default function FP2ESensorBrands() {
  const { t } = useTranslation();
  const endpoint = '/api/bconnect/webapp/administrator/references/fp2e-sensor-brands/';
  const [action, setAction] = useState<ModalWithLoaderAction>(null);
  const [requestStatus, setRequestStatus] = useState<RequestStatus>(null);
  const [id, setId] = useState<number | null>(null);
  const { data: clients } = useGetCompanies();
  const { confirmDialog, closeDialog, dialogOptions } = useDialog();
  const { refresh, triggerRefresh } = useRefresh();
  const { notif, notifOptions } = useNotif();

  const listFields = [
    {
      name: 'Code',
      label: t('referencesTables.code')
    },
    {
      name: 'Label',
      label: t('referencesTables.brand')
    },
    {
      name: 'Models',
      label: t('referencesTables.model'),
      transform: (_: any, row: any) => {
        if (!row.Models?.length) return '-';
        return (
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {row.Models.map((m: any, i: number) => (
              <li key={i}>
                Code : <strong>{m.Code ?? '-'}</strong> / Nom : <strong>{m.Label ?? '-'}</strong>
              </li>
            ))}
          </ul>
        );
      }
    },
    {
      name: 'companies',
      label: t('referencesTables.clients'),
      transform: (value: number[]) => {
        return value.length ? `${value.length} / ${clients?.length}` : '-';
      }
    }
  ];

  const actions = [
    {
      name: 'edit',
      label: t('referencesTables.sensorBrands.actions.edit'),
      render: (item: FP2EItem) => {
        return (
          <IconButton
            onClick={() => {
              setAction('edit');
              setId(item.Id);
            }}
          >
            <Edit fontSize="small" color="action" />
          </IconButton>
        );
      }
    },
    {
      name: 'delete',
      label: t('referencesTables.sensorBrands.actions.delete'),
      render: (param: FP2EItem) => {
        return (
          <IconButton
            onClick={() => {
              confirmDialog({
                title: t('referencesTables.sensorBrands.actions.delete'),
                content: t('referencesTables.sensorBrands.actions.deleteTypeContent'),
                onValidate: () => {
                  setAction('delete');
                  axios
                    .delete(
                      `/api/bconnect/webapp/administrator/references/fp2e-sensor-brands/${param.Id}/`
                    )
                    .then(
                      () => {
                        setAction(null);
                        triggerRefresh();
                        notif({
                          content: t('referencesTables.deleteSuccess'),
                          type: 'success'
                        });
                      },
                      () => {
                        setAction(null);
                        notif({
                          content: t('referencesTables.deleteError'),
                          type: 'success'
                        });
                      }
                    );
                  closeDialog();
                },
                onCancel: () => {
                  closeDialog();
                }
              });
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        );
      }
    }
  ];

  const handleCloseModalWithLoader = () => {
    setAction(null);
    setRequestStatus(null);
    setId(null);
  };

  useEffect(() => {
    if (requestStatus === 'success') {
      triggerRefresh();
      setId(null);
    }
  }, [requestStatus, triggerRefresh]);

  return (
    <>
      <BTable
        model={'fp2e-sensor-brands'}
        endpoint={endpoint}
        fields={listFields}
        actions={actions}
        refresh={refresh}
        headerActions={[
          {
            name: t('referencesTables.sensorBrands.actions.add'),
            element: (
              <IconButton onClick={() => setAction('add')}>
                <AddCircle fontSize="small" color="action" />
              </IconButton>
            )
          }
        ]}
      />

      <BirdzDialog options={dialogOptions} />
      <BirdzNotif options={notifOptions} />

      <ModalWithLoader
        openModal={action === 'add' || action === 'edit'}
        onClose={() => handleCloseModalWithLoader()}
        onSuccess={() => handleCloseModalWithLoader()}
        action={
          action === 'delete'
            ? t('referencesTables.sensorBrands.modalMessages.deleteTypeAction')
            : action === 'edit'
              ? t('referencesTables.sensorBrands.modalMessages.editTypeAction')
              : t('referencesTables.sensorBrands.modalMessages.createTypection')
        }
        status={requestStatus}
        setStatus={setRequestStatus}
        showCloseButton={true}
      >
        <FP2ESensorBrandForm
          endpoint={endpoint}
          setRequestStatus={setRequestStatus}
          action={action}
          setAction={setAction}
          id={id}
        />
      </ModalWithLoader>
    </>
  );
}
